import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect } from "react";
import Button from "./Button";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  size?: "sm" | "md" | "lg";
  children: ReactNode;
  footer?: ReactNode;
  confirmLabel?: string;
  onConfirm?: () => void;
  confirmLoading?: boolean;
  confirmVariant?: "primary" | "danger";
}

const sizeMap = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
};

export default function Modal({
  open,
  onClose,
  title,
  size = "md",
  children,
  footer,
  confirmLabel,
  onConfirm,
  confirmLoading,
  confirmVariant = "primary",
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4 animate-fadeIn"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`w-full ${sizeMap[size]} overflow-hidden rounded-[10px] border border-line bg-white shadow-dropdown animate-modalIn`}
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <h3 className="text-base font-semibold text-ink">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[calc(100vh-14rem)] overflow-y-auto px-5 py-4">{children}</div>

        {footer !== undefined ? (
          footer
        ) : (
          <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            {onConfirm && (
              <Button variant={confirmVariant} onClick={onConfirm} loading={confirmLoading}>
                {confirmLabel ?? "Confirm"}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}